import type { LandingProductCallbacks } from './landingProductDescriptor.types';
import type { LandingProductCopy } from './landingProductCopy';
import type { FmProductSectionDescriptor } from '@/components/ui/FmProductPage';
import type { Language } from '@/i18n';

const copy = {
  fi: {
    title: 'Näe, mistä metsätilasi arvo muodostuu.',
    answer: 'Arvo ei tule yhdestä mustasta laatikosta.',
    explanation:
      'Summa-arvomenetelmä kokoaa metsätilan osat yhteen. Näet sekä laskennan rakenteen että kunkin osan lähteen.',
    method: 'Summa-arvomenetelmä',
    total: 'Metsätilan perusteltu arvo',
    provenance: 'Jokainen osa säilyttää oman lähteen ja havaintoajankohdan.',
    components: [
      ['Puusto', 'Puustotiedot + puun hinnat', 'forest'],
      ['Taimikot', 'Taimikon kehitysvaihe ja kasvupaikka', 'teal'],
      ['Metsämaa', 'Kasvupaikka ja alueellinen taulukkoarvo', 'amber'],
      ['Perustellut korjaukset', 'Näkyvä peruste, ei piilokerrointa', 'sky'],
    ],
  },
  en: {
    title: "See what your forest property's value is built from.",
    answer: 'The value does not come from one black box.',
    explanation:
      "The Finnish summation method brings the property's parts together. You can inspect both the derivation and the source behind each part.",
    method: 'Finnish summation method',
    total: 'A reasoned forest-property value',
    provenance: 'Each component retains its source and observation date.',
    components: [
      ['Growing stock', 'Forest inventory + timber prices', 'forest'],
      ['Young stands', 'Development stage and site type', 'teal'],
      ['Forest land', 'Site type and regional table value', 'amber'],
      ['Justified adjustments', 'Visible basis, no hidden multiplier', 'sky'],
    ],
  },
  sv: {
    title: 'Se vad din skogsfastighets värde består av.',
    answer: 'Värdet kommer inte ur en svart låda.',
    explanation:
      'Summavärdesmetoden för samman fastighetens delar. Du kan granska både beräkningen och källan bakom varje del.',
    method: 'Summavärdesmetoden',
    total: 'Ett motiverat skogsfastighetsvärde',
    provenance: 'Varje del behåller sin källa och observationstid.',
    components: [
      ['Virkesförråd', 'Skogsdata + virkespriser', 'forest'],
      ['Plantskogar', 'Utvecklingsskede och ståndort', 'teal'],
      ['Skogsmark', 'Ståndort och regionalt tabellvärde', 'amber'],
      ['Motiverade justeringar', 'Synlig grund, ingen dold faktor', 'sky'],
    ],
  },
  es: {
    title: 'Descubre de qué se compone el valor de tu finca forestal.',
    answer: 'El valor no sale de una caja negra.',
    explanation:
      'El método finlandés de suma reúne las partes de la finca. Puedes revisar tanto el cálculo como la fuente de cada parte.',
    method: 'Método finlandés de suma',
    total: 'Un valor forestal fundamentado',
    provenance: 'Cada componente conserva su fuente y fecha de observación.',
    components: [
      ['Arbolado', 'Inventario forestal + precios de madera', 'forest'],
      ['Masas jóvenes', 'Fase de desarrollo y tipo de sitio', 'teal'],
      ['Suelo forestal', 'Tipo de sitio y valor regional tabulado', 'amber'],
      ['Ajustes justificados', 'Base visible, sin multiplicador oculto', 'sky'],
    ],
  },
} as const;

export function valuationMethodSection(locale: Language): FmProductSectionDescriptor {
  const finnish = locale === 'fi';
  const text = finnish ? copy.fi : copy.en;
  const scenes = finnish
    ? [
        [
          '00:00–00:07',
          'Eri kehitysvaiheet, eri arvon osat',
          'Taimikon, kasvatusmetsän ja uudistuskypsän metsän arvo muodostuu eri tavoin.',
        ],
        [
          '00:07–00:14',
          'Odotusarvo näkyviin',
          'Kasvatusmetsän hakkuuarvo ja erikseen perusteltu odotusarvo pidetään toisistaan erillään.',
        ],
        [
          '00:14–00:20',
          'Tiedon lähde ratkaisee',
          'Metsävaratiedon lähde, ajankohta ja kattavuus pysyvät tarkistettavina.',
        ],
        [
          '00:20–00:27',
          'Mittausten merkitys',
          'Läpimittajakauma ja puuston rakenne auttavat tarkentamaan puutavaralajeja.',
        ],
        [
          '00:27–00:34',
          'Alueellinen hintahistoria',
          'Luke-historia näyttää Keski-Suomen kuusitukin ja kuitupuun toteutuneen hintakehityksen vuosilta 2016–2025.',
        ],
        [
          '00:34–00:41',
          'Koko hinta-arvio',
          'Osat, kerran huomioitavat kulut ja perusteltu korjaus kootaan markkina-arvion pohjaksi.',
        ],
        [
          '00:41–00:47',
          'Vertaa samalla pohjalla',
          'Hinta-arviota, pyyntihintaa ja toista arviota verrataan tiedon, ajankohdan ja oletusten kautta.',
        ],
        [
          '00:47–00:52',
          'Näe perusteet ennen päätöstä',
          'Sinä arvioit kokonaisuuden ja päätät, miten etenet.',
        ],
      ]
    : [
        [
          '00:00–00:07',
          'Different stages, different components',
          'Young, growing, and mature stands are treated differently in the valuation.',
        ],
        [
          '00:07–00:14',
          'Make expectation value visible',
          'Cutting value and a separately justified expectation increment remain distinct.',
        ],
        [
          '00:14–00:20',
          'The source matters',
          'The inventory source, date, and coverage remain available for inspection.',
        ],
        [
          '00:20–00:27',
          'What measurements add',
          'Diameter distribution and stand structure help refine timber assortments.',
        ],
        [
          '00:27–00:34',
          'Regional price history',
          'Luke history shows realised Central Finland spruce sawlog and pulpwood prices for 2016–2025.',
        ],
        [
          '00:34–00:41',
          'The whole appraisal',
          'Components, costs counted once, and a justified adjustment form the basis of a market-value estimate.',
        ],
        [
          '00:41–00:47',
          'Compare on the same basis',
          'Review an appraisal, asking price, and another appraisal through their data, date, and assumptions.',
        ],
        [
          '00:47–00:52',
          'See the basis before deciding',
          'You assess the whole picture and decide how to proceed.',
        ],
      ];
  return {
    id: 'landing-owner-valuation',
    capability: 'registered-media',
    mediaKind: 'video',
    role: 'lead',
    title: text.title,
    state: { status: 'ready' },
    answer: finnish
      ? 'Näe, mistä metsätilasi hinta-arvio muodostuu.'
      : 'See what your forest-property estimate is built from.',
    explanation: finnish
      ? 'Tarkista puustotieto, kehitysvaiheet, alueelliset hinnat ja perustellut korjaukset ennen kuin vertaat hinta-arviota muihin lukuihin.'
      : 'Inspect the inventory, development stages, regional prices, and justified adjustments before comparing the estimate with other figures.',
    caption: finnish
      ? 'Havainnollistava video · 52 s · ei metsätila-arvio'
      : 'Illustrative film · 52 sec · method and product concept',
    mediaIdentity: finnish ? 'product.valuation-film.fi' : 'product.valuation-film.en',
    posterIdentity: finnish
      ? 'product.valuation-film.poster-fi'
      : 'product.valuation-film.poster-en',
    mobileMediaIdentity: finnish
      ? 'product.valuation-film.mobile-fi'
      : 'product.valuation-film.mobile-en',
    mobilePosterIdentity: finnish
      ? 'product.valuation-film.poster-mobile-fi'
      : 'product.valuation-film.poster-mobile-en',
    playLabel: finnish
      ? 'Toista hinta-arvion perusteita avaava video'
      : 'Play the forest valuation basis film',
    pauseLabel: finnish ? 'Keskeytä video' : 'Pause the film',
    transcriptLabel: finnish ? 'Lue videon sisältö' : 'Read the film transcript',
    transcriptTitle: finnish
      ? 'Mistä metsätilan hinta-arvio muodostuu'
      : 'What builds a forest-property estimate',
    transcriptScenes: scenes.map(([timeLabel, title, detail], index) => ({
      id: `valuation-film-scene-${index + 1}`,
      timeLabel,
      title,
      detail,
    })) as [
      { id: string; timeLabel: string; title: string; detail: string },
      ...{ id: string; timeLabel: string; title: string; detail: string }[],
    ],
  };
}

export function buyerValuationMethodCompanion(
  copy: LandingProductCopy,
  callbacks: LandingProductCallbacks,
): FmProductSectionDescriptor {
  const buyer = copy.valueStreams.buyer;
  return {
    id: 'landing-buyer-valuation',
    capability: 'contextual-actions',
    role: 'detail',
    title: buyer.title,
    state: { status: 'ready' },
    answer: buyer.description,
    context: buyer.tags.join(' · '),
    actions: [
      {
        id: 'landing-buyer-valuation-open',
        label: buyer.cta,
        intent: 'secondary',
        onSelect: callbacks.openBuyerValuation,
      },
    ],
  };
}
